<template>
  <div class="log-wrapper">
    <a-card class="log-card" :loading="loading" bordered>
      <template #title>
        <div class="title">
          <span>请求日志</span>
          <span class="sub">记录每一次搜索：时间、问题、来源与 IP</span>
        </div>
      </template>
      <template #extra>
        <a-space :size="10">
          <a-input
            v-model:value="keyword"
            placeholder="按问题关键字过滤"
            allow-clear
            style="width: 200px"
            @pressEnter="handleSearch"
          />
          <a-input
            v-model:value="source"
            placeholder="来源"
            allow-clear
            style="width: 160px"
            @pressEnter="handleSearch"
          />
          <a-input
            v-model:value="client"
            placeholder="客户端"
            allow-clear
            style="width: 160px"
            @pressEnter="handleSearch"
          />
          <a-segmented v-model:value="range" :options="rangeOptions" @change="handleSearch" />
        </a-space>
      </template>
      <a-table
        :data-source="dataSource"
        :columns="columns"
        :pagination="pagination"
        :row-key="record => record.id"
        @change="handleTableChange"
        size="middle"
      >
        <template #createTime="{ text }">
          {{ formatDate(text) }}
        </template>
        <template #question="{ record }">
          <div class="question-cell" :title="record.question">
            {{ record.question || '-' }}
          </div>
        </template>
        <template #result="{ record }">
          <a-tag :color="record.hasAnswer ? 'green' : 'orange'">
            {{ record.hasAnswer ? '命中' : '未命中' }}
          </a-tag>
        </template>
        <template #user="{ record }">
          <span v-if="canViewAllUsers">{{ record.authUsername || record.authUserId || '-' }}</span>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, onMounted, computed } from 'vue'
import { getRequestLogs } from '@/api/TikuComponentApi'
import { message } from 'ant-design-vue'
import { hasGlobalAccess, hasPermission, PERMISSIONS } from '@/utils/permissions'
import { asPageResult } from '@/utils/result'

export default defineComponent({
  name: 'RequestLog',
  setup() {
    const dataSource = ref([])
    const loading = ref(false)
    const keyword = ref('')
    const source = ref('')
    const client = ref('')
    const range = ref('7d')
    const rangeOptions = [
      { label: '24H', value: '24h' },
      { label: '7天', value: '7d' },
      { label: '14天', value: '14d' },
      { label: '全部', value: 'all' },
    ]
    const canReadRequestLogs = hasPermission(PERMISSIONS.REQUEST_LOG_READ)
    const canViewAllUsers = hasGlobalAccess() || hasPermission(PERMISSIONS.REQUEST_REVIEW)
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ['10', '20', '50', '100']
    })

    const buildStart = (val) => {
      const now = Date.now()
      switch (val) {
        case '24h':
          return new Date(now - 24 * 60 * 60 * 1000)
        case '7d':
          return new Date(now - 7 * 24 * 60 * 60 * 1000)
        case '14d':
          return new Date(now - 14 * 24 * 60 * 60 * 1000)
        default:
          return null
      }
    }

    const fetchData = () => {
      loading.value = true
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      const start = buildStart(range.value)
      if (start) params.start = start.toISOString()
      if (keyword.value) params.q = keyword.value
      if (source.value) params.source = source.value
      if (client.value) params.client = client.value
      getRequestLogs(params).then(res => {
        if (res.status === 200) {
          const pageResult = asPageResult(res.data, pagination.pageSize)
          dataSource.value = pageResult.data
          pagination.total = pageResult.total
          pagination.current = pageResult.page
          pagination.pageSize = pageResult.pageSize
        } else {
          dataSource.value = []
          message.error(res.message || '获取请求日志失败')
        }
      }).finally(() => {
        loading.value = false
      })
    }

    const handleTableChange = (pager) => {
      pagination.current = pager.current
      pagination.pageSize = pager.pageSize
      fetchData()
    }

    const handleSearch = () => {
      pagination.current = 1
      fetchData()
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '-'
      const Y = date.getFullYear()
      const M = String(date.getMonth() + 1).padStart(2, '0')
      const D = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${Y}-${M}-${D} ${h}:${m}:${s}`
    }

    const columns = computed(() => {
      const base = [
        { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 180, slots: { customRender: 'createTime' } },
        { title: '问题', dataIndex: 'question', key: 'question', ellipsis: true, slots: { customRender: 'question' } },
        { title: 'IP', dataIndex: 'clientIp', key: 'clientIp', width: 140 },
        { title: '来源', dataIndex: 'source', key: 'source', width: 120 },
        { title: '客户端', dataIndex: 'client', key: 'client', width: 120 },
        { title: '耗时(ms)', dataIndex: 'durationMs', key: 'durationMs', width: 110 },
        { title: '结果', dataIndex: 'hasAnswer', key: 'hasAnswer', width: 90, slots: { customRender: 'result' } },
      ]
      if (canViewAllUsers) {
        base.splice(1, 0, { title: '用户', dataIndex: 'authUsername', key: 'authUsername', width: 180, slots: { customRender: 'user' } })
      }
      return base
    })

    onMounted(() => {
      if (!canReadRequestLogs) {
        message.warning('无权限查看请求日志')
        return
      }
      fetchData()
    })

    return {
      dataSource,
      loading,
      pagination,
      keyword,
      source,
      client,
      range,
      rangeOptions,
      columns,
      formatDate,
      handleTableChange,
      handleSearch,
      canViewAllUsers
    }
  }
})
</script>

<style scoped>
.log-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.title {
  display: flex;
  flex-direction: column;
}

.title span:first-child {
  font-size: 18px;
  font-weight: 600;
  color: #1f1f1f;
}

.title .sub {
  color: #8c8c8c;
  font-size: 13px;
  margin-top: 4px;
  font-weight: normal;
}

.log-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
}

.question-cell {
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f0f7ff !important;
}
</style>
